import React, {useCallback, useMemo, useRef, useState} from "react";
import {Accordion, Button, Dropdown, Header, Modal} from "semantic-ui-react";
import { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  usePendingProjectMemberListSelector,
  useProjectMemberListSelector,
  useTaskforceListSelector,
} from "../../../../../../store/selectors/team.selectors";
import { useDispatch } from "react-redux";
import { TeamThunk } from "../../../../../../store/thunk/team.thunk";
import ProjectMembersModel from "../../../../../../models/responses/project-members.model";
import {
  useUserGradeSelector,
  useUserSelector,
} from "../../../../../../store/selectors/authorization.selectors";
import { MemberOrganizationEnum } from "../../../../../../models/member-organization.enum";
import { useActiveProjectSelector } from "../../../../../../store/selectors/project.selectors";
import { useActiveTaskSelector } from "../../../../../../store/selectors/task/task.selectors";
import { AddNewMemberFormValue } from "../HeaderMember";
import styles from "./ProjectTeam.module.scss";
import classNames from "classnames";
import { SearchActions } from "../../../../../../store/actions/search.actions";
import {
  faAngleDown,
  faAngleUp,
  faTasks,
  faUserTimes,
  faBalanceScaleLeft,
  faUserPlus,
  faEnvelope,
  faCheck,
  faUserCheck,
  faTransporter2,
  faUserGroupCrown
} from "@fortawesome/pro-light-svg-icons";
import {
  faGlasses,
  faAt,
  faUser,
  faUserGear,
  faUserGroup,
  faHourglass,
  faPersonToDoor
} from "@fortawesome/pro-solid-svg-icons";
import { GateKeeperEum } from "../../../../../../models/gate-keeper.enum";
import {addTaskOwner, joinTaskForce, leaveTaskForce} from "../../../../../../utils/taskforce.utils";
import FirebaseUsage from "../../../../../../firebase/firebase.usage";
import {PendingMembershipsModel} from "../../../../../../models/pending-memberships.model";
import FirebaseTeamFunctions from "../../../../../../firebase/firebase-functions/team-service";
import TeamDropdown from "./components/TeamDropdown";
import {COLLECTIONS} from "../../../../../../firebase/constants";

interface InjectedProps {
  searchValue: string;
  addNewTaskforceMember: (data: AddNewMemberFormValue) => void;
  removeTaskforceMember: (data: AddNewMemberFormValue) => void;
}

type PropsType = InjectedProps;

export const ProjectTeam: React.FC<PropsType> = (props) => {
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [clickedMember, setClickedMember] = useState<ProjectMembersModel>();
  const taskForceList = useTaskforceListSelector();
  const projectMembersList = useProjectMemberListSelector();
  const user = useUserSelector();
  const activeProject = useActiveProjectSelector();
  const userGrade = useUserGradeSelector();
  const pendingMembersList = usePendingProjectMemberListSelector()
  const [showTaskOwnerModal, setShowTaskOwnerModal] = useState<any>(null);
  const [hoverUser, setHoverUser] = useState<string | null>(null);
  const allMembers = [...projectMembersList, ...pendingMembersList];
  // const allMembers = projectMembersList

  const activeTask = useActiveTaskSelector();
  const dispatch = useDispatch();
  const combinedLists = useMemo(() => {
    const groupedByGrade = allMembers.filter(
        (member: any, index, self) => member.pendingId ?
            index ===
            self.findIndex(
                (m: any) => m.pendingId === member.pendingId
            ) &&
            (!props.searchValue ||
                member.userEmail.includes(props.searchValue)) :
            index ===
            self.findIndex(
                (m: any) => m.memberId === member.memberId
            ) &&
            (!props.searchValue ||
                member.userEmail.includes(props.searchValue))
    ).reduce((groups, member) => {
      const grade = member.grade;
      if (member.leftProject) {
        if (!groups[MemberOrganizationEnum.LEAVERS]) {
          groups[MemberOrganizationEnum.LEAVERS] = [];
        }
        groups[MemberOrganizationEnum.LEAVERS].push(member);
        return groups;
      }
      if (!groups[grade]) {
        groups[grade] = [];
      }
      groups[grade].push(member);
      return groups;
    }, {});

    const order = [
      MemberOrganizationEnum.SUPER_ACTOR,
      MemberOrganizationEnum.LEAD_ACTOR,
      MemberOrganizationEnum.ACTOR,
      MemberOrganizationEnum.OBSERVER,
      MemberOrganizationEnum.GROUPS,
      MemberOrganizationEnum.LEAVERS,
    ];

    return order.reduce((sorted, grade) => {
      if (groupedByGrade[grade]) {
        sorted[grade] = groupedByGrade[grade];
      }
      return sorted;
    }, {});
  }, [taskForceList, projectMembersList, pendingMembersList]);

  const [selectedMember, setSelectedMember] = useState<string>("");
  const [activeSecondaryTitle, setActiveSecondaryTitle] = useState<{
    index: number;
    grade: string;
    isActive: boolean | undefined;
  }>({
    index: -1,
    grade: "",
    isActive: false,
  });
  const [activeRole, setActiveRole] = useState<{
    index: number;
    isActive: boolean | undefined;
  }>({
    index: -1,
    isActive: false,
  });
  const [activeGatekeeper, setActiveGatekeeper] = useState<{
    index: number;
    isActive: boolean | undefined;
  }>({
    index: -1,
    isActive: false,
  });

  const reinstateMember = useCallback((member: any) => {
    if (member.userId) { // member is a ProjectMembersModel
      FirebaseUsage.updateDoc(COLLECTIONS.PROJECT_MEMBERS, member.memberId, {leftProject: false})
          .catch(e => console.error(e));
    } else {
        FirebaseUsage.updateDoc(COLLECTIONS.PENDING_MEMBERSHIPS, member.pendingId, {leftProject: false})
            .catch(e => console.error(e));
    }
  }, []);

  const projectMember = allMembers.find((member: any) => member.userId === selectedMember || member.pendingId === selectedMember);

  const mailToRedirect = useCallback(
    (member: ProjectMembersModel) => {
      if (
        activeTask &&
        user &&
        activeTask.taskForce.some(
          (el) => el === user.userId
        )) {
        window.location.href = `mailto: ${member.userEmail}?subject=${
          activeTask!.wbs
        }&body=${activeTask!.task_name}, ${activeTask!.task_code}`;
      } else {
        window.location.href = `mailto: ${member.userEmail}?subject=${
          activeProject!.name
        }`;
      }
    },
    [activeTask, user, activeProject]
  );

  const getMemberIconName = useCallback(
    (grade: MemberOrganizationEnum): IconDefinition => {
      switch (grade) {
        case MemberOrganizationEnum.OBSERVER: {
          return faGlasses;
        }
        case MemberOrganizationEnum.ACTOR: {
          return faUserGroup;
        }
        case MemberOrganizationEnum.LEAD_ACTOR: {
          return faUser;
        }
        case MemberOrganizationEnum.SUPER_ACTOR: {
          return faUserGear;
        }
        case MemberOrganizationEnum.GROUPS: {
          return faAt;
        }
        case MemberOrganizationEnum.LEAVERS: {
          return faPersonToDoor
        }
        default: {
          return faUser;
        }
      }
    },
    []
  );

  const accordionSecondaryTitleClickHandler =
    (titleIndex: number, userId: string, grade: string) => {
      setActiveSecondaryTitle({
        index: titleIndex,
        grade,
        isActive: userId !== selectedMember,
      })
      setSelectedMember(userId === selectedMember ? "" : userId);
    };

  const roleAccordionClickHandler = useCallback(
    (titleIndex: number) => {
      if (activeRole.index !== titleIndex) {
        setActiveRole({
          index: titleIndex,
          isActive: true,
        });
      } else {
        setActiveRole({
          ...activeRole,
          isActive: !activeRole.isActive,
        });
      }
    },
    [activeRole]
  );

  const gatekeeperAccordionClickHandler = useCallback(
    (titleIndex: number) => {
      if (activeGatekeeper.index !== titleIndex) {
        setActiveGatekeeper({
          index: titleIndex,
          isActive: true,
        });
      } else {
        setActiveGatekeeper({
          ...activeGatekeeper,
          isActive: !activeGatekeeper.isActive,
        });
      }
    },
    [activeGatekeeper]
  );

  const changeMemberGrade = useCallback(
    async (grade: MemberOrganizationEnum) => {
      if (!activeProject || !projectMember) {
        return null;
      }
      // @ts-ignore
      dispatch(TeamThunk.changeMemberGrade(grade, projectMember, activeProject.projectId));
    },
    [activeProject, dispatch, projectMember]
  );

  const changeGatekeeperGrade = useCallback(
    async (grade: GateKeeperEum) => {
      if (!activeProject || !projectMember) {
        return null;
      }
      // @ts-ignore
      dispatch(TeamThunk.changeMemberGateKeeper(grade, projectMember.memberId, activeProject.projectId));
    },
    [activeProject, dispatch, projectMember]
  );

  if (!activeProject) {
    return null;
  }

  return (
    <>
      <div className="accordion-wrapper">
        {Object.keys(combinedLists).length === 0 ? props.searchValue ?  (<span className="no-tasks">No team members match this search</span>)
            : (
          <span className="no-tasks">No project list entry</span>
        ) : (
            Object.entries(combinedLists).map(([grade, members]: [any, any], i) => (
                  <div key={grade}>
                    <div className={styles.GradeGroup}>
                      <FontAwesomeIcon
                        icon={getMemberIconName(grade)}
                        className={styles.MemberIcon}
                      />
                      <span>
                        {grade === MemberOrganizationEnum.ACTOR
                          ? "Team-Member"
                          : grade === MemberOrganizationEnum.LEAD_ACTOR
                          ? "Lead"
                          : grade === MemberOrganizationEnum.SUPER_ACTOR
                          ? "Administator"
                          : grade}
                      </span>
                    </div>
                {grade === MemberOrganizationEnum.LEAVERS ? userGrade[MemberOrganizationEnum.SUPER_ACTOR] ? members.map((member, index) => (
                    <div
                        key={`${member.memberId}-${index}`}
                        onMouseEnter={() => setHoverUser(member.userId || member.pendingId)}
                        onMouseLeave={() => setHoverUser(null)}
                        className={classNames(styles.MemberWrap, {
                          [styles.SelectedMember]: selectedMember === member.userId || selectedMember === member.pendingId,
                        })}
                    >
                      <div
                          className={classNames(styles.MemberItemWrap)}
                          onClick={() => setSelectedMember(prevState =>
                              prevState === member.userId || prevState === member.pendingId ?
                                  "" :
                                  member.userId || member.pendingId)}
                      >
                        <div className={styles.MemberIconWrap}>
                          {!member.userId && <FontAwesomeIcon
                              className={"pending-icon"}
                              icon={faHourglass}
                          />}
                        </div>
                        <span className={classNames(styles.MemberEmailLabel, styles.LeftMember,
                            {[styles.bold]: selectedMember === member.userId || selectedMember === member.pendingId}
                        )}>
                                {member.userEmail}
                              </span>
                        {(hoverUser === member.userId || hoverUser === member.pendingId) &&
                            <div
                                style={{
                                  fontSize: '18px',
                                  display: 'flex',
                                  flexDirection: 'row',
                                  flex: 1,
                                  textAlign: 'right',
                                  alignItems: 'flex-end',
                                  justifyContent: 'flex-end',
                                }}>
                              <FontAwesomeIcon
                                  style={{marginRight: '10px'}}
                                  icon={
                                    selectedMember === member.userId || selectedMember === member.pendingId ?
                                        faAngleUp :
                                        faAngleDown

                                  }
                              /></div>}
                      </div>
                      {selectedMember === member.userId || selectedMember === member.pendingId ? <>
                        <div
                            className={styles.MemberItemOption}
                            onClick={() => {
                              dispatch(
                                  SearchActions.setTaskforceFilter([[member.userId, member.userEmail]])
                              );
                              dispatch(
                                  SearchActions.setTaskOwnerFilter([member.userId, member.userEmail])
                              );
                            }}
                        >
                          <FontAwesomeIcon icon={faTasks} className={styles.MemberIconWrap} />
                          <span>View Tasks</span>
                        </div>
                        {member.grade !== MemberOrganizationEnum.GROUPS && <div
                            className={styles.MemberItemOption}
                            onClick={() => {
                              dispatch(
                                  SearchActions.setTaskOwnerFilter([member.userId, member.userEmail])
                              );
                            }}
                        >
                          <FontAwesomeIcon icon={faTasks} className={styles.MemberIconWrap} />
                          <span>View Owned Tasks</span>
                        </div>}
                        {userGrade[MemberOrganizationEnum.SUPER_ACTOR] && member.grade !== MemberOrganizationEnum.GROUPS ? (
                            <div
                                className={styles.MemberItemOption}
                                onClick={() => reinstateMember(member)}
                            >
                              <FontAwesomeIcon icon={faUserCheck} className={styles.MemberIconWrap} />
                              <span>Reinstate Project Member</span>
                            </div>
                        ) : null}
                        {userGrade[MemberOrganizationEnum.SUPER_ACTOR] && member.grade !== MemberOrganizationEnum.GROUPS ? (
                            <TeamDropdown
                                member={member}
                                icon={faUserGroupCrown}
                                title='Reassign Owned Tasks'
                                type={'taskOwner'}
                                onClick={(
                                  newEmail: string,
                                ) => FirebaseTeamFunctions.reassignTasks({
                                      oldEmail: member.userEmail,
                                      newEmail,
                                      projectId: activeProject.projectId,
                                      type: "taskOwner"
                                    })} />
                        ) : null}
                        {userGrade[MemberOrganizationEnum.SUPER_ACTOR] && member.grade !== MemberOrganizationEnum.GROUPS ? (
                                <TeamDropdown
                                    member={member}
                                    icon={faTransporter2}
                                    title='Reassign Tasks Memberships'
                                    type={'taskForce'}
                                    onClick={(
                                      newEmail: string,
                                    ) => FirebaseTeamFunctions.reassignTasks({
                                      oldEmail: member.userEmail,
                                      newEmail,
                                      projectId: activeProject.projectId,
                                      type: "taskForce"
                                    })} />
                        ) : null}
                      </>: null}
                    </div>
                )) : null : members.map((member, index) => (
                    <div
                        key={`${member.memberId}-${index}`}
                        onMouseEnter={() => setHoverUser(member.userId || member.pendingId)}
                        onMouseLeave={() => setHoverUser(null)}
                        className={classNames(styles.MemberWrap, {
                              [styles.SelectedMember]: selectedMember === member.userId || selectedMember === member.pendingId,
                            })}
                        >
                            <div
                              className={classNames(styles.MemberItemWrap)}
                              onClick={() => setSelectedMember(prevState =>
                                  prevState === member.userId || prevState === member.pendingId ?
                                      "" :
                                      member.userId || member.pendingId)}
                            >
                              <div className={styles.MemberIconWrap}>
                                {!member.userId && <FontAwesomeIcon
                                  className={"pending-icon"}
                                  icon={faHourglass}
                                />}
                              </div>
                              <span className={classNames(styles.MemberEmailLabel,
                                  {[styles.bold]: selectedMember === member.userId || selectedMember === member.pendingId}
                              )}>
                                {member.userEmail}
                              </span>
                              {(hoverUser === member.userId || hoverUser === member.pendingId) &&
                                  <div
                                      style={{
                                        fontSize: '18px',
                                        display: 'flex',
                                        flexDirection: 'row',
                                        flex: 1,
                                        textAlign: 'right',
                                        alignItems: 'flex-end',
                                        justifyContent: 'flex-end',
                                  }}>
                                  <FontAwesomeIcon
                                      style={{marginRight: '10px'}}
                                    icon={
                                      selectedMember === member.userId || selectedMember === member.pendingId ?
                                          faAngleUp :
                                          faAngleDown

                                    }
                                  /></div>}
                            </div>
                      {selectedMember === member.userId || selectedMember === member.pendingId ? <>
                          <div
                              className={classNames(styles.MemberItemOption, {
                                disabled: !Boolean(activeTask),
                              })}
                          >
                            <FontAwesomeIcon icon={faUserPlus} className={styles.MemberIconWrap} />
                            <span
                                onClick={() => {
                                  if (activeTask && activeTask.taskForce.includes(member.userId || member.userEmail)) {
                                    leaveTaskForce(activeTask.task_id, member.userId, member.userEmail, user!.userId!, FirebaseUsage.timestamp())
                                        .catch(e => console.error(e));
                                  } else if (activeTask) {
                                    joinTaskForce(activeTask.task_id, member.userId, member.userEmail, user!.userId!, FirebaseUsage.timestamp())
                                        .catch(e => console.error(e));
                                  }
                                }}
                            >{`${
                                activeTask &&
                                activeTask.taskForce.includes(member.userId)
                                    ? "Remove from"
                                    : "Add to"
                            } taskforce (${
                                activeTask
                                    ? activeTask.task_code
                                    : "*task code"
                            })`}</span>
                          </div>
                          {userGrade[MemberOrganizationEnum.SUPER_ACTOR] && member.grade !== MemberOrganizationEnum.GROUPS &&  <div
                              className={classNames(styles.MemberItemOption, {
                                disabled: !Boolean(activeTask),
                              })}
                          >
                            <FontAwesomeIcon icon={faUserPlus} className={styles.MemberIconWrap} />
                            <span
                                onClick={() => {
                                  if (activeTask && activeTask.taskOwner) {
                                    setShowTaskOwnerModal({
                                      incoming: member,
                                      outgoing: projectMembersList.find(m => m.userId === activeTask.taskOwner || m.userEmail === activeTask.taskOwner),
                                    });
                                  } else if (activeTask) {
                                    addTaskOwner(activeTask, activeTask.task_id, member.userEmail, member.userId, user!.userId!, activeProject.projectId)
                                        .catch(e => console.error(e));
                                  }
                                }}
                            >{`${
                                activeTask &&
                                (activeTask.taskOwner === member.userId || activeTask.taskOwner === member.userEmail)
                                    ? "Remove as"
                                    : "Add as"
                            } task owner (${
                                activeTask
                                    ? activeTask.task_code
                                    : "*task code"
                            })`}</span>
                          </div>}
                          <div
                              className={styles.MemberItemOption}
                              onClick={() => {
                                dispatch(
                                    SearchActions.setTaskforceFilter([[member.userId, member.userEmail]])
                                );
                                dispatch(
                                    SearchActions.setTaskOwnerFilter([member.userId, member.userEmail])
                                );
                              }}
                          >
                            <FontAwesomeIcon icon={faTasks} className={styles.MemberIconWrap} />
                            <span>View Tasks</span>
                          </div>
                          {member.grade !== MemberOrganizationEnum.GROUPS && <div
                              className={styles.MemberItemOption}
                              onClick={() => {
                                dispatch(
                                    SearchActions.setTaskOwnerFilter([member.userId, member.userEmail])
                                );
                              }}
                          >
                            <FontAwesomeIcon icon={faTasks} className={styles.MemberIconWrap} />
                            <span>View Owned Tasks</span>
                          </div>}
                          {member.grade ===
                          MemberOrganizationEnum.SUPER_ACTOR ? (
                              <div
                                  className={styles.MemberItemOption}
                                  onClick={() =>
                                      dispatch(
                                          // @ts-ignore
                                          TeamThunk.makeEvaluatorToggle(
                                              member.memberId
                                          )
                                      )
                                  }
                              >
                                <FontAwesomeIcon icon={faBalanceScaleLeft} className={styles.MemberIconWrap} />
                                <span>
                                    {member.gateKeeper
                                        ? "Remove evaluator priviledge"
                                        : "Make evaluator priviledge"}
                                  </span>
                              </div>
                          ) : null}
                          {member.grade !==
                              MemberOrganizationEnum.GROUPS && (
                                  <Accordion styled className="sub-accordion">
                                    <Accordion.Title>
                                      <div
                                          onClick={() =>
                                              gatekeeperAccordionClickHandler(
                                                  member.grade + index
                                              )
                                          }
                                          className="sub-title"
                                      >
                                      <span className={styles.roleLabel}>
                                        Gatekeeper
                                      </span>
                                        <FontAwesomeIcon
                                            icon={
                                              activeGatekeeper.index ===
                                              member.grade + index &&
                                              activeGatekeeper.isActive
                                                  ? faAngleUp
                                                  : faAngleDown
                                            }
                                        />
                                      </div>
                                    </Accordion.Title>
                                    <Accordion.Content
                                        active={
                                            activeGatekeeper.index ===
                                            member.grade + index &&
                                            activeGatekeeper.isActive
                                        }
                                        className={
                                          userGrade[
                                              MemberOrganizationEnum.SUPER_ACTOR
                                              ] ||
                                          userGrade[
                                              MemberOrganizationEnum.LEAD_ACTOR
                                              ]
                                              ? ""
                                              : "disabled"
                                        }
                                    >
                                      <div
                                          className="sub-accordion-label"
                                          onClick={() =>
                                              changeGatekeeperGrade(
                                                  GateKeeperEum.LockChecklist
                                              )
                                          }
                                      >
                                        <span>Lock checklist</span>
                                        {member.gateKeeper ===
                                            GateKeeperEum.LockChecklist && (
                                                <FontAwesomeIcon icon={faCheck} />
                                            )}
                                      </div>
                                      <div
                                          className="sub-accordion-label"
                                          onClick={() =>
                                              changeGatekeeperGrade(
                                                  GateKeeperEum.TaskSignOff
                                              )
                                          }
                                      >
                                        <span>Task sign-off</span>
                                        {member.gateKeeper ===
                                            GateKeeperEum.TaskSignOff && (
                                                <FontAwesomeIcon icon={faCheck} />
                                            )}
                                      </div>
                                      <div
                                          className="sub-accordion-label"
                                          onClick={() =>
                                              changeGatekeeperGrade(
                                                  GateKeeperEum.Both
                                              )
                                          }
                                      >
                                        <span>Both</span>
                                        {member.gateKeeper ===
                                            GateKeeperEum.Both && (
                                                <FontAwesomeIcon icon={faCheck} />
                                            )}
                                      </div>
                                      <div
                                          className="sub-accordion-label"
                                          onClick={() =>
                                              changeGatekeeperGrade(
                                                  GateKeeperEum.None
                                              )
                                          }
                                      >
                                        <span>None</span>
                                        {member.gateKeeper ===
                                            GateKeeperEum.None && (
                                                <FontAwesomeIcon icon={faCheck} />
                                            )}
                                      </div>
                                    </Accordion.Content>
                                  </Accordion>
                              )}
                          {member.grade !== MemberOrganizationEnum.GROUPS &&
                          (userGrade[MemberOrganizationEnum.SUPER_ACTOR] ||
                              userGrade[
                                  MemberOrganizationEnum.LEAD_ACTOR
                                  ]) ? (
                              <>
                                <Accordion styled className="sub-accordion">
                                  <Accordion.Title>
                                    <div
                                        onClick={() =>
                                            roleAccordionClickHandler(
                                                member.grade + index
                                            )
                                        }
                                        className="sub-title"
                                    >
                                        <span className={styles.roleLabel}>
                                          Role
                                        </span>
                                      <FontAwesomeIcon
                                          icon={
                                            activeRole.index ===
                                            member.grade + index &&
                                            activeRole.isActive
                                                ? faAngleUp
                                                : faAngleDown
                                          }
                                      />
                                    </div>
                                  </Accordion.Title>
                                  <Accordion.Content
                                      active={
                                          activeRole.index ===
                                          member.grade + index &&
                                          activeRole.isActive
                                      }
                                  >
                                    <div
                                        className="sub-accordion-label"
                                        onClick={() =>
                                            changeMemberGrade(
                                                MemberOrganizationEnum.SUPER_ACTOR
                                            )
                                        }
                                    >
                                      <span>Administrator</span>
                                      {member.grade ===
                                          MemberOrganizationEnum.SUPER_ACTOR && (
                                              <FontAwesomeIcon icon={faCheck} />
                                          )}
                                    </div>
                                    <div
                                        className="sub-accordion-label"
                                        onClick={() =>
                                            changeMemberGrade(
                                                MemberOrganizationEnum.LEAD_ACTOR
                                            )
                                        }
                                    >
                                      <span>Lead</span>
                                      {member.grade ===
                                          MemberOrganizationEnum.LEAD_ACTOR && (
                                              <FontAwesomeIcon icon={faCheck} />
                                          )}
                                    </div>
                                    <div
                                        className="sub-accordion-label"
                                        onClick={() =>
                                            changeMemberGrade(
                                                MemberOrganizationEnum.ACTOR
                                            )
                                        }
                                    >
                                      <span>Team member</span>
                                      {member.grade ===
                                          MemberOrganizationEnum.ACTOR && (
                                              <FontAwesomeIcon icon={faCheck} />
                                          )}
                                    </div>
                                    <div
                                        className="sub-accordion-label"
                                        onClick={() =>
                                            changeMemberGrade(
                                                MemberOrganizationEnum.OBSERVER
                                            )
                                        }
                                    >
                                      <span>Observer</span>
                                      {member.grade ===
                                          MemberOrganizationEnum.OBSERVER && (
                                              <FontAwesomeIcon icon={faCheck} />
                                          )}
                                    </div>
                                    <div
                                        className="sub-accordion-label"
                                        onClick={() =>
                                            changeMemberGrade(
                                                MemberOrganizationEnum.GROUPS
                                            )
                                        }
                                    >
                                      <span>Groups</span>
                                      {member.grade ===
                                          MemberOrganizationEnum.GROUPS && (
                                              <FontAwesomeIcon icon={faCheck} />
                                          )}
                                    </div>
                                  </Accordion.Content>
                                </Accordion>
                              </>
                          ) : null}
                          {member.grade !==
                              MemberOrganizationEnum.GROUPS && (
                                  <div
                                      className={styles.MemberItemOption}
                                      onClick={() => mailToRedirect(member)}
                                  >
                                    <FontAwesomeIcon icon={faEnvelope} className={styles.MemberIconWrap} />
                                    <span>Send an email</span>
                                  </div>
                              )}
                          {userGrade[MemberOrganizationEnum.SUPER_ACTOR] ||
                          userGrade[MemberOrganizationEnum.LEAD_ACTOR] ? (
                              <div
                                  className={styles.MemberItemOption}
                                  onClick={() => {
                                    if (
                                        member.userId === user?.userId &&
                                        projectMembersList.length === 1
                                    ) {
                                      setOpenModal(true);
                                      setClickedMember(member);
                                    } else {
                                      dispatch(
                                          // @ts-ignore
                                          TeamThunk.removeMember(
                                              member.userEmail,
                                              activeProject.contractId!,
                                              user!.userId!,
                                          )
                                      );
                                    }
                                  }}
                              >
                                <FontAwesomeIcon icon={faUserTimes} className={styles.MemberIconWrap} />
                                <span>Remove from pull system</span>
                              </div>
                          ) : null}
                        </>: null}
                    </div>
                  ))
              }
            </div>
            ))
        )}
        </div>

      <Modal dimmer="blurring" className="modal-event" open={openModal}>
        <Header
          textAlign="center"
          content={
            <h3>
              You are the only Administrator on this project, assign a
              replacement or the project will be closed
            </h3>
          }
        />
        <Modal.Actions>
          <Button negative onClick={() => setOpenModal(false)}>
            Cancel
          </Button>
          <Button
            positive
            onClick={() => {
              if (clickedMember) {
                dispatch(
                  // @ts-ignore
                  TeamThunk.removeMember(
                    clickedMember?.userEmail,
                    activeProject.contractId!,
                    user!.userId!
                  )
                );
                setOpenModal(false);
              }
            }}
          >
            Close Anyway
          </Button>
        </Modal.Actions>
      </Modal>
      {showTaskOwnerModal && activeTask && <Modal dimmer="blurring" className="modal-event" open={showTaskOwnerModal}>
        <Header
            textAlign="center"
            content={
              <h3>
                {showTaskOwnerModal.outgoing.userEmail} is already assigned as Task Owner. If you continue the current Task Owner will be replaced. Do you want to continue?
              </h3>
            }
        />
        <Modal.Actions>
          <Button negative onClick={() => setShowTaskOwnerModal(null)}>
            Cancel
          </Button>
          <Button
              positive
              onClick={() =>
                addTaskOwner(activeTask, activeTask.task_id, showTaskOwnerModal.incoming.userEmail, showTaskOwnerModal.incoming.userId, user!.userId!, activeProject.projectId)
                    .then(() => setShowTaskOwnerModal(null))
              }>
            Continue
          </Button>
        </Modal.Actions>
      </Modal>}
    </>
  );
};
